// Pure-canvas chart rendering → reliable dataURL images for email embedding (no html2canvas)
import { parseNumber, parseYearMonth, formatCompact } from "./utils";

export const CHART_COLORS = ["#1A73E8", "#188038", "#E37400", "#D93025", "#9334E6", "#12B5CB", "#80868B"];

const setupCanvas = (w, h) => {
  const canvas = document.createElement("canvas");
  const scale = 2;
  canvas.width = w * scale;
  canvas.height = h * scale;
  const ctx = canvas.getContext("2d");
  ctx.scale(scale, scale);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, w, h);
  return { canvas, ctx };
};

const FONT = "'Helvetica Neue', Arial, sans-serif";

// Donut chart with side legend. data: [{name, value}]
export const drawDonut = (data, { title = "" } = {}) => {
  const W = 580, H = 260;
  const { canvas, ctx } = setupCanvas(W, H);
  const total = data.reduce((s, d) => s + d.value, 0);
  if (!total) return null;

  if (title) {
    ctx.fillStyle = "#202124";
    ctx.font = `600 14px ${FONT}`;
    ctx.fillText(title, 16, 26);
  }

  const cx = 130, cy = H / 2 + (title ? 10 : 0), rOut = 88, rIn = 52;
  let angle = -Math.PI / 2;
  data.forEach((d, i) => {
    const slice = (d.value / total) * Math.PI * 2;
    ctx.beginPath();
    ctx.arc(cx, cy, rOut, angle, angle + slice);
    ctx.arc(cx, cy, rIn, angle + slice, angle, true);
    ctx.closePath();
    ctx.fillStyle = CHART_COLORS[i % CHART_COLORS.length];
    ctx.fill();
    // % label inside slice if big enough
    const pct = (d.value / total) * 100;
    if (pct >= 6) {
      const mid = angle + slice / 2;
      const lr = (rOut + rIn) / 2;
      ctx.fillStyle = "#ffffff";
      ctx.font = `700 11px ${FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(`${pct.toFixed(0)}%`, cx + Math.cos(mid) * lr, cy + Math.sin(mid) * lr);
      ctx.textAlign = "left";
      ctx.textBaseline = "alphabetic";
    }
    angle += slice;
  });

  // Legend
  const lx = 270;
  let ly = cy - (data.length * 24) / 2 + 8;
  data.forEach((d, i) => {
    ctx.fillStyle = CHART_COLORS[i % CHART_COLORS.length];
    ctx.beginPath();
    ctx.arc(lx + 6, ly - 4, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#202124";
    ctx.font = `500 13px ${FONT}`;
    const name = d.name.length > 22 ? d.name.slice(0, 21) + "…" : d.name;
    ctx.fillText(name, lx + 20, ly);
    ctx.fillStyle = "#5F6368";
    ctx.font = `400 13px ${FONT}`;
    ctx.fillText(`${((d.value / total) * 100).toFixed(1)}%`, lx + 210, ly);
    ly += 24;
  });

  return canvas.toDataURL("image/png");
};

// Dual-series trend (independent normalization per series — shows shape, labels show values)
// points: [{label, values: {seriesKey: number}}], seriesDefs: [{key, label, color, isCurrency}]
export const drawDualTrend = (points, seriesDefs, { title = "", currency = "USD" } = {}) => {
  const valid = points.filter((p) => seriesDefs.every((s) => p.values[s.key] !== null && p.values[s.key] !== undefined));
  if (valid.length < 2) return null;
  const W = 580, H = 270;
  const { canvas, ctx } = setupCanvas(W, H);
  const padL = 46, padR = 46, padT = 62, padB = 42;
  const x = (i) => padL + (i / (valid.length - 1)) * (W - padL - padR);

  if (title) {
    ctx.fillStyle = "#202124";
    ctx.font = `600 14px ${FONT}`;
    ctx.fillText(title, 16, 26);
  }
  // Legend (top-left under title)
  let lx = 16;
  seriesDefs.forEach((s) => {
    ctx.fillStyle = s.color;
    ctx.beginPath();
    ctx.arc(lx + 5, 42, 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#5F6368";
    ctx.font = `600 12px ${FONT}`;
    ctx.fillText(s.label, lx + 15, 46);
    lx += 15 + ctx.measureText(s.label).width + 24;
  });

  seriesDefs.forEach((s, si) => {
    const vals = valid.map((p) => p.values[s.key]);
    const min = Math.min(...vals), max = Math.max(...vals);
    const range = max - min || 1;
    const y = (v) => padT + (1 - (v - min + range * 0.18) / (range * 1.4)) * (H - padT - padB);
    ctx.beginPath();
    ctx.moveTo(x(0), y(vals[0]));
    vals.forEach((v, i) => ctx.lineTo(x(i), y(v)));
    ctx.strokeStyle = s.color;
    ctx.lineWidth = 2.5;
    ctx.lineJoin = "round";
    ctx.stroke();
    const showVals = valid.length <= 7; // avoid label collisions on long histories
    ctx.textAlign = "center";
    vals.forEach((v, i) => {
      ctx.beginPath();
      ctx.arc(x(i), y(v), valid.length > 8 ? 3 : 4, 0, Math.PI * 2);
      ctx.fillStyle = "#ffffff";
      ctx.fill();
      ctx.strokeStyle = s.color;
      ctx.lineWidth = 2;
      ctx.stroke();
      if (showVals || i === vals.length - 1) {
        ctx.fillStyle = s.color;
        ctx.font = `700 11px ${FONT}`;
        ctx.fillText(formatCompact(v, { isCurrency: s.isCurrency, currency }), x(i), y(v) + (si === 0 ? -12 : 20));
      }
    });
    ctx.textAlign = "left";
  });

  // X labels (thin out when many points)
  const labelStep = valid.length > 8 ? 2 : 1;
  ctx.textAlign = "center";
  valid.forEach((p, i) => {
    if (i % labelStep !== 0 && i !== valid.length - 1) return;
    ctx.fillStyle = "#5F6368";
    ctx.font = `400 11px ${FONT}`;
    ctx.fillText(p.label, x(i), H - padB + 24);
  });
  ctx.textAlign = "left";

  return canvas.toDataURL("image/png");
};

// Revenue trendline. history: sorted KPI rows, key: metric column
export const drawTrend = (history, key, { title = "", isCurrency = false, currency = "USD" } = {}) => {
  const rows = history.filter((r) => parseNumber(r[key]) !== null);
  if (rows.length < 2) return null;
  const W = 580, H = 240;
  const { canvas, ctx } = setupCanvas(W, H);
  const padL = 20, padR = 20, padT = title ? 48 : 28, padB = 40;
  const values = rows.map((r) => parseNumber(r[key]));
  const min = Math.min(...values), max = Math.max(...values);
  const range = max - min || 1;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const x = (i) => padL + (i / (rows.length - 1)) * plotW;
  const y = (v) => padT + (1 - (v - min + range * 0.12) / (range * 1.3)) * plotH;

  if (title) {
    ctx.fillStyle = "#202124";
    ctx.font = `600 14px ${FONT}`;
    ctx.fillText(title, 16, 26);
  }

  // Area fill
  ctx.beginPath();
  ctx.moveTo(x(0), y(values[0]));
  values.forEach((v, i) => ctx.lineTo(x(i), y(v)));
  ctx.lineTo(x(rows.length - 1), H - padB);
  ctx.lineTo(x(0), H - padB);
  ctx.closePath();
  const grad = ctx.createLinearGradient(0, padT, 0, H - padB);
  grad.addColorStop(0, "rgba(26,115,232,0.18)");
  grad.addColorStop(1, "rgba(26,115,232,0.01)");
  ctx.fillStyle = grad;
  ctx.fill();

  // Line
  ctx.beginPath();
  ctx.moveTo(x(0), y(values[0]));
  values.forEach((v, i) => ctx.lineTo(x(i), y(v)));
  ctx.strokeStyle = "#1A73E8";
  ctx.lineWidth = 2.5;
  ctx.lineJoin = "round";
  ctx.stroke();

  // Dots + value labels + month labels
  ctx.textAlign = "center";
  values.forEach((v, i) => {
    ctx.beginPath();
    ctx.arc(x(i), y(v), 4, 0, Math.PI * 2);
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.strokeStyle = "#1A73E8";
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = "#202124";
    ctx.font = `600 11px ${FONT}`;
    ctx.fillText(formatCompact(v, { isCurrency, currency }), x(i), y(v) - 12);
    const p = parseYearMonth(rows[i]["Year month"]);
    ctx.fillStyle = "#5F6368";
    ctx.font = `400 11px ${FONT}`;
    ctx.fillText(p ? `${p.month} ${p.year.slice(2)}` : "", x(i), H - padB + 22);
  });
  ctx.textAlign = "left";

  return canvas.toDataURL("image/png");
};
